'use client';

import type { ComponentProps } from 'react';
import { Pencil } from 'lucide-react';

import { Badge } from '@/shared/ui/shadcn/badge';
import { Button } from '@/shared/ui/shadcn/button';
import { cn } from '@/lib/utils';

export interface RxDraftSummaryNodeProps extends ComponentProps<'section'> {
  medicationDisplayName: string;
  directions: string;
  quantity: string;
  refills: string;
  daysSupply: string;
  pharmacyDisplayName?: string;
  pharmacyNotes?: string;
  noSubstitutions?: boolean;
  diagnosis?: { code: string; name: string } | null;
  onEdit?: () => void;
}

export function RxDraftSummaryNode({
  medicationDisplayName,
  directions,
  quantity,
  refills,
  daysSupply,
  pharmacyDisplayName,
  pharmacyNotes,
  noSubstitutions,
  diagnosis,
  onEdit,
  className,
  ...props
}: RxDraftSummaryNodeProps) {
  const trimmedQuantity = quantity.trim();
  const trimmedRefills = refills.trim();
  const trimmedDaysSupply = daysSupply.trim();
  const trimmedPharmacyDisplayName = pharmacyDisplayName?.trim() ?? '';
  const trimmedPharmacyNotes = pharmacyNotes?.trim() ?? '';
  const diagnosisValue = diagnosis
    ? `${diagnosis.code}${diagnosis.name ? ` — ${diagnosis.name}` : ''}`
    : '—';
  const detailRows: Array<{ label: string; value: string }> = [
    { label: 'Quantity', value: trimmedQuantity.length > 0 ? quantity : '—' },
    { label: 'Refills', value: trimmedRefills.length > 0 ? refills : '—' },
    { label: 'Days supply', value: trimmedDaysSupply.length > 0 ? daysSupply : '—' },
    {
      label: 'Pharmacy',
      value: trimmedPharmacyDisplayName.length > 0 ? trimmedPharmacyDisplayName : '—',
    },
    { label: 'Notes', value: trimmedPharmacyNotes.length > 0 ? trimmedPharmacyNotes : '—' },
    { label: 'Diagnosis', value: diagnosisValue },
  ];

  return (
    <section className={cn('space-y-3 text-sm', className)} {...props}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium leading-snug">{medicationDisplayName}</p>
        </div>

        {onEdit ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onEdit}
            className="h-6 gap-1 px-2 text-xs"
          >
            <Pencil className="size-3" aria-hidden />
            Edit
          </Button>
        ) : null}
      </div>

      <div className="border-border border-y py-2">
        <p className="whitespace-pre-wrap">{directions}</p>
      </div>

      <div className="space-y-2">
        {detailRows.map((row) => (
          <div key={row.label} className="flex items-start justify-between gap-3">
            <span className="font-mono text-[11px] tracking-[0.08em] text-muted-foreground uppercase">
              {row.label}
            </span>
            <span className="max-w-[70%] whitespace-pre-wrap text-right">{row.value}</span>
          </div>
        ))}
      </div>

      <div className="border-t pt-2">
        <Badge variant={noSubstitutions ? 'secondary' : 'outline'}>
          {noSubstitutions ? 'No substitutions' : 'Allow substitutions'}
        </Badge>
      </div>
    </section>
  );
}
